import _ from "lodash";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

import { breakpoints as _breakpoints } from "../ui";

interface ScreenBreakpoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

interface ScreenContext {
  width: number;
  height: number;
  resizing: number;
  size: number | null;
}

const ScreenContext = createContext<ScreenContext | null>(null);

// Provider
export const ScreenProvider = ({
  children,
  breakpoints = _breakpoints,
}: {
  children: any;
  breakpoints?: ScreenBreakpoints;
}) => {
  const [width, setWidth] = useState(
    typeof window === "undefined" ? 0 : window.innerWidth
  );
  const [height, setHeight] = useState(
    typeof window === "undefined" ? 0 : window.innerHeight
  );
  const [resizing, setResizing] = useState(
    typeof window === "undefined" ? 0 : 0
  );
  const [size, setSize] = useState<number | null>(null);

  const _onResizeChange = () => {
    setResizing((state) => {
      if (state >= 1000) {
        return 0;
      } else {
        return typeof state === "number" ? state + 1 : 0;
      }
    });
  };

  useEffect(() => {
    const debounceHandleResize = _.debounce(function (window: any) {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
      _onResizeChange();

      setTimeout(() => {
        setResizing(0);
      }, 100);
    }, 100);

    window.addEventListener("resize", () => debounceHandleResize(window));

    return () => {
      window.removeEventListener("resize", () => debounceHandleResize(window));
    };
  }, []);

  useLayoutEffect(() => {
    if (width >= breakpoints.xl) {
      setSize(5);
    } else if (width >= breakpoints.lg) {
      setSize(4);
    } else if (width >= breakpoints.md) {
      setSize(3);
    } else if (width >= breakpoints.sm) {
      setSize(2);
    } else {
      setSize(1);
    }
  }, [width]);

  useEffect(() => {
    const rootElement = document?.getElementById("root");

    if (rootElement) {
      rootElement.removeAttribute("data-size");

      if (size) {
        if (size >= 5) {
          rootElement.setAttribute("data-size", "desktop");
        } else if (size >= 3) {
          rootElement.setAttribute("data-size", "tablet");
        } else {
          rootElement.setAttribute("data-size", "mobile");
        }
      }
    }
  }, [width]);

  return (
    <ScreenContext.Provider
      value={{
        width,
        height,
        resizing,
        size,
      }}
    >
      {children}
    </ScreenContext.Provider>
  );
};

// Listener
export const useScreenListener = () => {
  return useContext(ScreenContext) as ScreenContext;
};
